import { CalendarOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { BasketballTeam, CustomTableColumn } from '@hefe/models';
import {
  BasketballCoachesSlice,
  BasketballTeamsSlice,
  BasketballUsersSlice,
  GymsSlice,
  ModalsSlice,
  currentUserSelector,
} from '@hefe/store';
import { formatDate } from '@hefe/utils';
import { Button, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import TeamsMoreActionsComponent from '../../components/teams/teams-more-actions/teams-more-actions.component';
import AddTeamForm from '../../forms/add-team/add-team.form';
import SyncPracticesUntilForm from '../../forms/sync-practices-until/sync-practices-until.form';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface TeamsPageProps {}

export default function TeamsPage(props: TeamsPageProps) {
  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);
  const teams = useSelector(BasketballTeamsSlice.basketballTeamsSelector);
  const coaches = useSelector(
    BasketballCoachesSlice.basketballCoachesSelectSelector
  );
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  useEffect(() => {
    fetchTeams();
    fetchCoaches();
    fetchGyms();
    fetchAllUsers();
  }, []);

  const fetchTeams = () => {
    setLoading(true);
    dispatch(BasketballTeamsSlice.fetchBasketballTeams())
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  };

  const fetchCoaches = () => {
    dispatch(BasketballCoachesSlice.fetchBasketballCoaches())
      .unwrap()
      .catch(() => null);
  };

  const fetchGyms = () => {
    dispatch(GymsSlice.fetchGyms({ cacheDuration: 5 * 60 * 1000 }))
      .unwrap()
      .catch(() => null);
  };

  const fetchAllUsers = () => {
    dispatch(
      BasketballUsersSlice.fetchBasketballUsersSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const getCoaches = (): string[] => {
    const returnValue = new Set<string>();

    teams?.forEach((team) => {
      team.coaches?.forEach((coach) => {
        returnValue.add(`${coach.firstname} ${coach.lastname}`);
      });
    });

    return Array.from(returnValue);
  };

  const openSyncPracticesUntilModal = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <SyncPracticesUntilForm teamIds={selectedRowKeys} />,
        title: t('practices.sync.until'),
        formId: 'sync-practices-until-form',
      })
    );
  };

  const addTeamBtnDisabledTooptip = useMemo(() => {
    const teamsLimit = user?.club_subscription?.limits.basketball_teams;

    if (teamsLimit && teams.length + 1 > teamsLimit) {
      return t('errors.permissions.basketball_teams');
    }

    return undefined;
  }, [user?.club_subscription?.limits.basketball_teams, teams.length]);

  const columns: CustomTableColumn[] = [
    {
      key: 'name',
      title: t('name'),
      dataIndex: 'name',
      sortType: 'string',
      showFilter: true,
      default: true,
    },
    {
      key: 'coaches',
      title: t('coaches'),
      default: true,
      render: (team: BasketballTeam) => {
        return team.coaches
          ?.map((coach) => `${coach.firstname} ${coach.lastname}`)
          .join(', ');
      },
      filters: getCoaches().map((coach) => {
        return {
          value: coach,
          text: coach,
        };
      }),
      onFilter: (value: string, record: BasketballTeam) => {
        return record.coaches
          ?.map((coach) => `${coach.firstname} ${coach.lastname}`)
          .includes(value);
      },
    },
    {
      key: 'sync_practices_until',
      dataIndex: 'sync_practices_until',
      title: t('practices.sync.until'),
      default: true,
      render: (date: string) => {
        return date ? formatDate(date) : '---';
      },
    },
    {
      title: '...',
      key: 'more',
      default: true,
      render: (item: BasketballTeam) => {
        return <TeamsMoreActionsComponent team={item} coaches={coaches} />;
      },
    },
  ];

  return (
    <CustomTable
      rowKey={'id'}
      columns={columns}
      dataSource={teams}
      name="teams"
      loading={loading}
      rowSelection
      selectedRowKeys={selectedRowKeys}
      onRowSelect={(selectedRowKeys) => setSelectedRowKeys(selectedRowKeys)}
      addForm={{
        component: <AddTeamForm />,
        title: t('add_team'),
        formId: 'add-team-form',
        disabledTooltip: addTeamBtnDisabledTooptip,
      }}
      toolbarItems={[
        <Tooltip title={t('practices.sync.title')}>
          <Button
            icon={<CalendarOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => openSyncPracticesUntilModal()}
          />
        </Tooltip>,
      ]}
    />
  );
}
